import { useState } from 'react';
import { useTheme } from '../lib/theme-provider';
import { ThemedCard } from './ThemedCard';
import { ThemedButton } from './ThemedButton';
import { BookOpen, X, Users, Shield, Sword, Crown, Search } from 'lucide-react';

interface GameRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type RuleTab = 'basic' | 'roles' | 'extensions';

export default function GameRulesModal({ isOpen, onClose }: GameRulesModalProps) {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<RuleTab>('basic');

  if (!isOpen) return null;

  const tabs: { id: RuleTab; label: string; icon: typeof Users }[] = [
    { id: 'basic', label: '基础规则', icon: BookOpen },
    { id: 'roles', label: '角色说明', icon: Users },
    { id: 'extensions', label: '扩展规则', icon: Shield },
  ];

  const renderBasicRules = () => (
    <div className="space-y-4">
      <section>
        <h3
          className="text-base font-bold mb-2 flex items-center gap-2"
          style={{ color: theme.colors['accent-gold'] }}
        >
          <Crown size={18} />
          游戏目标
        </h3>
        <ul
          className="space-y-2 text-sm"
          style={{ color: theme.colors['text-secondary'] }}
        >
          <li>
            <strong style={{ color: theme.colors['accent-green'] }}>
              正义阵营
            </strong>
            ：成功完成3次任务，或保护梅林不被刺客发现
          </li>
          <li>
            <strong style={{ color: theme.colors['accent-red'] }}>
              邪恶阵营
            </strong>
            ：破坏3次任务，或在游戏结束时刺杀梅林
          </li>
        </ul>
      </section>

      <section>
        <h3
          className="text-base font-bold mb-2 flex items-center gap-2"
          style={{ color: theme.colors['accent-gold'] }}
        >
          <Users size={18} />
          游戏流程
        </h3>
        <ol
          className="space-y-2 text-sm list-decimal list-inside"
          style={{ color: theme.colors['text-secondary'] }}
        >
          <li>队长选择玩家组成任务队伍</li>
          <li>所有玩家投票是否同意该队伍</li>
          <li>同意票数过半则队伍执行任务</li>
          <li>任务队员选择成功或失败</li>
          <li>根据结果进入下一轮</li>
          <li>先完成3次任务的一方获胜</li>
        </ol>
      </section>

      <section>
        <h3
          className="text-base font-bold mb-2 flex items-center gap-2"
          style={{ color: theme.colors['accent-gold'] }}
        >
          <Shield size={18} />
          任务配置
        </h3>
        <div
          className="grid grid-cols-5 gap-2 text-center text-sm"
          style={{ color: theme.colors['text-secondary'] }}
        >
          <div className="p-2 rounded" style={{ backgroundColor: theme.colors['bg-primary'] }}>
            <div className="font-bold">第1轮</div>
            <div>3人</div>
          </div>
          <div className="p-2 rounded" style={{ backgroundColor: theme.colors['bg-primary'] }}>
            <div className="font-bold">第2轮</div>
            <div>4人</div>
          </div>
          <div className="p-2 rounded" style={{ backgroundColor: theme.colors['bg-primary'] }}>
            <div className="font-bold">第3轮</div>
            <div>4人</div>
          </div>
          <div className="p-2 rounded" style={{ backgroundColor: theme.colors['bg-primary'] }}>
            <div className="font-bold">第4轮</div>
            <div>5人</div>
            <div className="text-xs" style={{ color: theme.colors['accent-red'] }}>需2失败</div>
          </div>
          <div className="p-2 rounded" style={{ backgroundColor: theme.colors['bg-primary'] }}>
            <div className="font-bold">第5轮</div>
            <div>5人</div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderRoles = () => (
    <div className="space-y-4">
      <section>
        <h3
          className="text-base font-bold mb-2 flex items-center gap-2"
          style={{ color: theme.colors['accent-green'] }}
        >
          <Shield size={18} />
          正义阵营
        </h3>
        <div className="space-y-3">
          {[
            { name: '梅林', desc: '知道所有邪恶阵营玩家，但不能暴露自己' },
            { name: '派西维尔', desc: '可以看到梅林和莫甘娜，需要分辨真假' },
            { name: '忠臣', desc: '正义阵营的普通成员，帮助梅林隐藏' },
          ].map((role) => (
            <div
              key={role.name}
              className="p-3 rounded-lg"
              style={{ backgroundColor: theme.colors['bg-primary'] }}
            >
              <div
                className="font-medium mb-1"
                style={{ color: theme.colors['accent-green'] }}
              >
                {role.name}
              </div>
              <div
                className="text-sm"
                style={{ color: theme.colors['text-secondary'] }}
              >
                {role.desc}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3
          className="text-base font-bold mb-2 flex items-center gap-2"
          style={{ color: theme.colors['accent-red'] }}
        >
          <Sword size={18} />
          邪恶阵营
        </h3>
        <div className="space-y-3">
          {[
            { name: '刺客', desc: '游戏结束时可以刺杀一名玩家' },
            { name: '莫甘娜', desc: '看起来像梅林，可以迷惑派西维尔' },
            { name: '莫德雷德', desc: '梅林看不到你的身份' },
            { name: '奥伯伦', desc: '其他邪恶阵营看不到你的身份' },
          ].map((role) => (
            <div
              key={role.name}
              className="p-3 rounded-lg"
              style={{ backgroundColor: theme.colors['bg-primary'] }}
            >
              <div
                className="font-medium mb-1"
                style={{ color: theme.colors['accent-red'] }}
              >
                {role.name}
              </div>
              <div
                className="text-sm"
                style={{ color: theme.colors['text-secondary'] }}
              >
                {role.desc}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );

  const renderExtensions = () => (
    <div className="space-y-4">
      <section>
        <h3
          className="text-base font-bold mb-2 flex items-center gap-2"
          style={{ color: theme.colors['accent-gold'] }}
        >
          <Search size={18} />
          湖中仙女
        </h3>
        <div
          className="p-3 rounded-lg text-sm"
          style={{
            backgroundColor: theme.colors['bg-primary'],
            color: theme.colors['text-secondary'],
          }}
        >
          任务成功后可选择一名玩家查看其阵营。只能查看未被查看过的玩家。
        </div>
      </section>

      <section>
        <h3
          className="text-base font-bold mb-2 flex items-center gap-2"
          style={{ color: theme.colors['accent-gold'] }}
        >
          <Sword size={18} />
          王者之剑
        </h3>
        <div
          className="p-3 rounded-lg text-sm"
          style={{
            backgroundColor: theme.colors['bg-primary'],
            color: theme.colors['text-secondary'],
          }}
        >
          可以反转一名任务队员的任务结果（成功变失败，失败变成功）。
        </div>
      </section>

      <section>
        <h3
          className="text-base font-bold mb-2 flex items-center gap-2"
          style={{ color: theme.colors['accent-gold'] }}
        >
          <Shield size={18} />
          强制任务
        </h3>
        <div
          className="p-3 rounded-lg text-sm"
          style={{
            backgroundColor: theme.colors['bg-primary'],
            color: theme.colors['text-secondary'],
          }}
        >
          第3轮或第5轮必须派出指定玩家参与任务。
        </div>
      </section>

      <section>
        <h3
          className="text-base font-bold mb-2 flex items-center gap-2"
          style={{ color: theme.colors['accent-gold'] }}
        >
          <Users size={18} />
          公开投票
        </h3>
        <div
          className="p-3 rounded-lg text-sm"
          style={{
            backgroundColor: theme.colors['bg-primary'],
            color: theme.colors['text-secondary'],
          }}
        >
          所有玩家的投票结果对所有人可见。
        </div>
      </section>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}
    >
      <ThemedCard className="w-full max-w-md max-h-[80vh] p-6 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div
            className="flex items-center gap-2"
            style={{ color: theme.colors['accent-gold'] }}
          >
            <BookOpen size={24} />
            <h2
              className="text-xl font-bold"
              style={{ color: theme.colors['text-primary'] }}
            >
              游戏规则
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ backgroundColor: theme.colors['bg-primary'] }}
          >
            <X size={20} style={{ color: theme.colors['text-secondary'] }} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors"
              style={{
                backgroundColor:
                  activeTab === tab.id
                    ? `${theme.colors['accent-gold']}20`
                    : theme.colors['bg-primary'],
                border: `1px solid ${
                  activeTab === tab.id
                    ? theme.colors['accent-gold']
                    : theme.colors['border-secondary']
                }`,
                color:
                  activeTab === tab.id
                    ? theme.colors['accent-gold']
                    : theme.colors['text-secondary'],
              }}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div
          className="overflow-y-auto max-h-[50vh] pr-2"
          style={{ scrollbarWidth: 'thin' }}
        >
          {activeTab === 'basic' && renderBasicRules()}
          {activeTab === 'roles' && renderRoles()}
          {activeTab === 'extensions' && renderExtensions()}
        </div>

        {/* Close Button */}
        <ThemedButton onClick={onClose} className="w-full mt-4">
          <X size={18} className="mr-1" />
          关闭
        </ThemedButton>
      </ThemedCard>
    </div>
  );
}
